import { lazy, Suspense, useMemo, useState } from "react";
import { Menu, LogOut } from "lucide-react";
import {
  Outlet,
  Route,
  Routes,
  useLocation,
  NavLink,
  useNavigate,
} from "react-router-dom";
const LoginPage = lazy(() => import("@/pages/Login"));
const RegisterPage = lazy(() => import("@/pages/Register"));
const RegisterSuccessPage = lazy(() => import("@/pages/RegisterSuccess"));
const ActivateUserPage = lazy(() => import("@/pages/ActivateUser"));
const NotFoundPage = lazy(() => import("@/pages/NotFound"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Transactions = lazy(() => import("@/pages/Transactions"));
const Budgets = lazy(() => import("@/pages/Budgets"));
const Recebiveis = lazy(() => import("@/pages/Recebiveis"));
const ContasPagar = lazy(() => import("@/pages/ContasPagar"));
const Profile = lazy(() => import("@/pages/Profile"));
import { sidebarItems } from "@/components/layout/SidebarItems";
import Sidebar from "@/components/layout/Sidebar";
import { ThemeToggle } from "@/components/shared/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { ProtectedRoute } from "./context/ProtectedRoute";
import { useAuth } from "@/hooks/useAuth";
import ErrorBoundary from "./components/shared/ErrorBoundary";

const LayoutHeader = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  const title = useMemo(() => {
    switch (location.pathname) {
      case "/":
        return "Painel de Controle";
      case "/transactions":
        return "Transações";
      case "/budgets":
        return "Orçamentos";
      case "/recebiveis":
        return "Recebíveis";
      case "/contaspagar":
        return "Contas a Pagar";
      case "/profile":
        return "Meu Perfil";
      default:
        return "Financeiro";
    }
  }, [location.pathname]);

  const greeting = useMemo(() => {
    const now = new Date().getHours();
    const userName = user?.name?.split(" ")[0] || "";
    const namePrefix = userName ? `, ${userName}` : "";
    
    if (now < 12) return `Bom dia${namePrefix}`;
    if (now < 18) return `Boa tarde${namePrefix}`;
    return `Boa noite${namePrefix}`;
  }, [user?.name]);

  return (
    <header className="hidden items-center justify-between border-b border-border px-4 py-3 sm:px-6 sm:py-4 lg:flex">
      <div className="flex-1 overflow-hidden">
        <p className="text-xs sm:text-sm font-medium text-muted-foreground truncate">
          {greeting}, bem-vindo de volta!
        </p>
        <h1 className="text-lg sm:text-xl font-semibold truncate">{title}</h1>
      </div>
      <div className="ml-4 flex items-center gap-2 shrink-0">
        <ThemeToggle />
      </div>
    </header>
  );
};

const MobileNavigation = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const title = useMemo(() => {
    switch (location.pathname) {
      case "/":
        return "Painel de Controle";
      case "/transactions":
        return "Transações";
      case "/budgets":
        return "Orçamentos";
      case "/recebiveis":
        return "Recebíveis";
      case "/contaspagar":
        return "Contas a Pagar";
      case "/profile":
        return "Meu Perfil";
      default:
        return "Financeiro";
    }
  }, [location.pathname]);

  const handleLogout = () => {
    const confirmLogout = window.confirm("Tem certeza que deseja sair?");
    if (confirmLogout) {
      logout();
      navigate("/login");
      setOpen(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 border-b border-border bg-card/50 sm:flex-row sm:items-center sm:justify-between sm:gap-0 lg:hidden">
      {/* Logo Section */}
      <div className="flex items-center gap-2 px-4 py-3">
        <span className="text-xl sm:text-2xl">💰</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-muted-foreground sm:hidden">
            Painel Financeiro
          </p>
          <p className="truncate text-sm font-semibold">{title}</p>
        </div>
      </div>

      {/* Actions Section */}
      <div className="flex items-center justify-between gap-2 px-4 pb-3 sm:pb-0 sm:pr-4">
        <ThemeToggle />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Abrir menu de navegação"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-11/12 max-w-sm gap-4 rounded-lg">
            <SheetHeader className="space-y-1">
              <SheetTitle>Navegação</SheetTitle>
              <SheetDescription>
                Escolha um módulo para visualizar.
              </SheetDescription>
            </SheetHeader>
            <nav className="space-y-1 max-h-[60vh] overflow-y-auto">
              {sidebarItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
                      isActive &&
                        "bg-primary text-primary-foreground hover:bg-primary",
                    )
                  }
                  onClick={() => setOpen(false)}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-border pt-3">
              {user && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Logado como: <span className="font-medium text-foreground">{user.name}</span>
                </div>
              )}
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 shrink-0" />
                Sair
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
};

const AppLayout = () => (
  <div className="flex min-h-screen flex-col bg-background text-foreground lg:flex-row">
    <Sidebar />
    <div className="flex flex-1 flex-col overflow-hidden">
      <MobileNavigation />
      <LayoutHeader />
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-4 sm:py-6 lg:px-6">
          <Outlet />
        </div>
      </main>
    </div>
  </div>
);

const App = () => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div className="p-4 text-center">Carregando...</div>}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register-success" element={<RegisterSuccessPage />} />
          <Route path="/activate/:token" element={<ActivateUserPage />} />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="/transactions" element={<Transactions />} />
            <Route path="/budgets" element={<Budgets />} />
            <Route path="/recebiveis" element={<Recebiveis />} />
            <Route path="/contaspagar" element={<ContasPagar />} />
            <Route path="/profile" element={<Profile />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
